import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@examdojo/core/environment';
import { mapToVoid } from '@examdojo/core/rxjs';
import { UserHttpModel } from '@examdojo/models/user';
import { ExamdojoSupabaseService, TableUpdateModel } from '@examdojo/supabase';
import { from, Observable } from 'rxjs';

const userInvitationUrl = `${environment.examdojo.studentsApiUrl}/invite`;

export const AVATARS_STORAGE_BUCKET_NAME = 'avatars';

@Injectable({ providedIn: 'root' })
export class UserHttpService {
  constructor(
    private readonly supabase: ExamdojoSupabaseService,
    private readonly http: HttpClient,
  ) {}

  fetch(id: UserHttpModel['id']) {
    return from(
      this.supabase.client
        .from('users')
        .select()
        .eq('id', id)
        .throwOnError()
        .then((res) => res.data!),
    );
  }

  fetchActiveUserProfile() {
    return from(
      this.supabase.client
        .rpc('get_current_user_profile')
        .throwOnError()
        .then((res) => res.data!),
    );
  }

  update(id: UserHttpModel['id'], user: TableUpdateModel<'users'>): Observable<UserHttpModel> {
    return from(
      this.supabase.client
        .from('users')
        .update({
          ...user,
        })
        .eq('id', id)
        .select()
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  invite(invitationData: { email: string }) {
    return this.http.post(userInvitationUrl, { students: [invitationData] }).pipe(mapToVoid());
  }

  deleteProfile() {
    return this.http.post(`${environment.examdojo.studentsApiUrl}/soft-delete`, {}).pipe(mapToVoid());
  }

  uploadProfilePicture(picture: File | ArrayBuffer | Blob, fileType: string, fileName: string) {
    return this.supabase.uploadFile(AVATARS_STORAGE_BUCKET_NAME, picture, fileType, fileName);
  }

  deleteProfilePicture(fileName: string) {
    return this.supabase.removeFiles(AVATARS_STORAGE_BUCKET_NAME, fileName);
  }

  fetchByIds(userIds: string[]) {
    if (userIds.length === 0) {
      return from(Promise.resolve([]));
    }

    return from(
      this.supabase.client
        .from('users')
        .select()
        .in('id', userIds)
        .throwOnError()
        .then((res) => res.data || []),
    );
  }

  getProfilePicturePublicUrl(imageId: string) {
    return this.supabase.getPublicUrl(AVATARS_STORAGE_BUCKET_NAME, imageId);
  }
}
